import React from 'react';
import './MarkdownTextRenderer.scss';

interface MarkdownTextRendererProps {
  text: string;
  className?: string;
}

const MarkdownTextRenderer: React.FC<MarkdownTextRendererProps> = ({ text, className = '' }) => {
  const parseText = (inputText: string): React.ReactNode[] => {
    if (!inputText) return [];

    // Trim trailing empty lines to prevent unnecessary breaks at the end
    const trimmedText = inputText.replace(/\n+$/, '');
    const lines = trimmedText.split('\n');
    const elements: React.ReactNode[] = [];
    
    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) {
        i++;
        continue;
      }

      // Handle headers (##)
      if (line.startsWith('##') && !line.startsWith('###')) {
        const headerText = line.replace(/^#+\s*/, '');
        elements.push(
          <h2 key={`header-${i}`} className="markdown-header">
            {parseInlineFormatting(headerText)}
          </h2>
        );
        i++;
      }
      // Handle subheaders (###)
      else if (line.startsWith('###')) {
        const headerText = line.replace(/^#+\s*/, '');
        elements.push(
          <h3 key={`subheader-${i}`} className="markdown-subheader">
            {parseInlineFormatting(headerText)}
          </h3>
        );
        i++;
      }
      // Handle numbered lists (group consecutive items)
      else if (/^\d+\.\s/.test(line)) {
        const numberedItems: React.ReactNode[] = [];
        const startIndex = i;
        let listCounter = 1;
        
        // Collect all consecutive numbered items
        while (i < lines.length) {
          const currentLine = lines[i].trim();
          if (!currentLine) {
            i++;
            continue;
          }
          if (/^\d+\.\s/.test(currentLine)) {
            const listText = currentLine.replace(/^\d+\.\s/, '');
            numberedItems.push(
              <div key={`numbered-${i}`} className="markdown-numbered-item">
                <span className="markdown-number">{listCounter}.</span>
                <span className="markdown-numbered-content">
                  {parseInlineFormatting(listText)}
                </span>
              </div>
            );
            listCounter++;
            i++;
          } else {
            break;
          }
        }
        
        // Wrap the group in a container
        elements.push(
          <div key={`numbered-group-${startIndex}`} className="markdown-numbered-list">
            {numberedItems}
          </div>
        );
      }
      // Handle bullet points (group consecutive items)
      else if (line.startsWith('- ')) {
        const bulletItems: React.ReactNode[] = [];
        const startIndex = i;
        
        // Collect all consecutive bullet items
        while (i < lines.length) {
          const currentLine = lines[i].trim();
          if (!currentLine) {
            i++;
            continue;
          }
          if (currentLine.startsWith('- ')) {
            const bulletText = currentLine.replace(/^-\s/, '');
            bulletItems.push(
              <div key={`bullet-${i}`} className="markdown-bullet-item">
                <span className="markdown-bullet">•</span>
                <span className="markdown-bullet-content">
                  {parseInlineFormatting(bulletText)}
                </span>
              </div>
            );
            i++;
          } else {
            break;
          }
        }
        
        // Wrap the group in a container
        elements.push(
          <div key={`bullet-group-${startIndex}`} className="markdown-bullet-list">
            {bulletItems}
          </div>
        );
      }
      // Handle regular paragraphs
      else {
        elements.push(
          <p key={`paragraph-${i}`} className="markdown-paragraph">
            {parseInlineFormatting(line)}
          </p>
        );
        i++;
      }
    }

    return elements;
  };

  const parseInlineFormatting = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let keyCounter = 0;

    // Regex to match **bold** text (non-greedy match)
    const boldRegex = /\*\*([^*]+)\*\*/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the bold section
      if (match.index > currentIndex) {
        parts.push(
          <span key={`text-${keyCounter++}`}>
            {text.slice(currentIndex, match.index)}
          </span>
        );
      }

      // Add the bold text
      parts.push(
        <strong key={`bold-${keyCounter++}`} className="markdown-bold">
          {match[1]}
        </strong>
      );

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(
        <span key={`text-${keyCounter++}`}>
          {text.slice(currentIndex)}
        </span>
      );
    }

    return parts.length > 0 ? parts : [text];
  };

  return (
    <div className={`markdown-text-renderer ${className}`}>
      {parseText(text)}
    </div>
  );
};

export default MarkdownTextRenderer;
